// MACHINERY V2 (Stroje) — Servisné správy: what each kebab entry DOES (M2.5).
//
// Split out of ServiceReportsTabV2.tsx for the 300-line cap. That file owns the
// states and the pixels (the query moved to useSrTabQuery.ts at the M2.5
// review); this owns navigation, the delete flow and the share target.
// srTabActions.ts (next door) owns which entries EXIST.
//
// REPOINTED AT M2.6. Detail / create / edit now open the V2 pages
// (`/machinery-v2/:id/service-reports/...`) through sr/srNavigation.ts, which is
// the single place those paths are built. The ONE deliberate exception is the
// SOLVE wizard: §9 D5 rules it is not re-skinned this cycle, so `srSolvePath`
// still deep-links to the LIVE `/machinery/...solve` page — now carrying the
// M2.6 review R4 return token so that page comes BACK to v2 (see srNavigation.ts).
//
// TWO SHARED-SLOT WRITES ARE LOAD-BEARING, NOT LEFTOVERS. The create/edit page
// reads `customerServiceReports.serviceReport` to decide whether it is a create
// or an edit form, so v1 dispatches `selectServiceReport(item)` before Edit and
// `selectServiceReport(null)` before Create (`handleEdit`, ServiceReports.tsx:190-193, and
// `handleCreateServiceReport`, :167-170). Dropping either turns Create into an
// edit of whatever was last selected. Both writes now live in srNavigation.ts —
// `openSrEdit` / `openSrCreate` — so no caller can perform half of the pair.
//
// A REFUSED CONFIRM IS SPOKEN ALOUD. useV2Confirm returns false when another v2
// confirmation is already open; swallowing that is what makes a menu item look
// broken, so it becomes an error toast.
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "../../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../../shared/hooks/useTranslation";
import publicServiceReportsApi from "../../../../../public/api/publicServiceReportsApi";
import { deleteServiceReport } from "../../../customerMachineServiceReportsSlice";
import { setError, setSuccess } from "../../../customerMachinerySlice";
import { ServiceReport } from "../../../models/ServiceReportsModel";
import useV2Confirm from "../../feedback/useV2Confirm";
import {
  openSrCreate,
  openSrEdit,
  srDetailPath,
  srSolvePath,
} from "../../sr/srNavigation";
import { SrActionHandlers } from "./srTabActions";

export interface SrTabActions {
  handlers: SrActionHandlers;
  /** Opens the v2 create form for this machine. */
  createReport: () => void;
  shareTarget: ServiceReport | null;
  closeShare: () => void;
}

export const useSrTabActions = (machineId: string, refetch: () => void): SrTabActions => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const confirm = useV2Confirm();
  const { translate } = useTranslation("myMachinery");
  const [shareTarget, setShareTarget] = useState<ServiceReport | null>(null);

  const requestDelete = useCallback(
    (r: ServiceReport) => {
      const opened = confirm({
        title: translate("myMachinery:deleteServiceReportTitle"),
        text: translate("myMachinery:deleteServiceReportText", { number: r.number }),
        confirmButton: translate("myMachinery:yesDelete"),
        closeButton: translate("common:common.no"),
        severity: "error",
        onConfirm: async () => {
          try {
            await dispatch(deleteServiceReport(r.number)).unwrap();
            dispatch(
              setSuccess(translate("myMachinery:v2.tabs.sr.deleted", { number: r.number }))
            );
          } catch {
            dispatch(setError(translate("myMachinery:v2.tabs.sr.deleteFailed")));
          }
          refetch();
        },
      });
      if (!opened) dispatch(setError(translate("myMachinery:delete.confirmBusy")));
    },
    [confirm, dispatch, refetch, translate]
  );

  const handlers = useMemo<SrActionHandlers>(
    () => ({
      onShowDetails: (r) => navigate(srDetailPath(machineId, r.number)),
      onShare: (r) => setShareTarget(r),
      onDownloadPdf: (r) =>
        publicServiceReportsApi.getServiceReportPdfLink(
          r.number,
          `service-report-${r.number}.pdf`
        ),
      onEdit: (r) => openSrEdit(dispatch, navigate, machineId, r),
      // §9 D5: the solve wizard is NOT re-skinned this cycle — both entries
      // deep-link to the existing live page. `srSolvePath` appends R4's fixed
      // `?returnTo=v2` TOKEN (not a path — see srNavigation.ts), which is what
      // makes that page's Save and Cancel land on /machinery-v2/:id instead of
      // the v1 profile.
      onMarkAsSolved: (r) => navigate(srSolvePath(machineId, r.number)),
      onEditSolvedReport: (r) => navigate(srSolvePath(machineId, r.number)),
      onCreateServiceRequest: (r) =>
        navigate(
          `/service-requests/new?machine=${machineId}&report=${r.number}` +
            `&returnTo=/machinery-v2/${machineId}`
        ),
      onDelete: requestDelete,
    }),
    [dispatch, machineId, navigate, requestDelete]
  );

  const createReport = useCallback(() => {
    openSrCreate(dispatch, navigate, machineId);
  }, [dispatch, machineId, navigate]);

  const closeShare = useCallback(() => setShareTarget(null), []);

  return { handlers, createReport, shareTarget, closeShare };
};

export default useSrTabActions;
